import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Logo, { LogoMark } from "@/components/Logo";
import { EASE } from "@/components/Reveal";

function Line({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Stamp() {
  return (
    <div className="w-28 h-28 lg:w-32 lg:h-32 rounded-full bg-clay text-cream grid place-items-center animate-spin-slow shadow-lg">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <path id="stamp-circle" d="M 50,50 m -35,0 a 35,35 0 1,1 70,0 a 35,35 0 1,1 -70,0" />
        </defs>
        <text fontSize="9" letterSpacing="2.6" fill="currentColor">
          <textPath href="#stamp-circle">WELL & TRULY · BY AKANKSHA · DUBAI ·</textPath>
        </text>
        <text x="50" y="57" textAnchor="middle" fontSize="20" fill="currentColor" fontStyle="italic" fontFamily="Fraunces, serif">
          &
        </text>
      </svg>
    </div>
  );
}

const LINKS = [
  { href: "#specialties", label: "What I help with", tid: "nav-link-specialties" },
  { href: "#programs", label: "Programs", tid: "nav-link-programs" },
  { href: "#about", label: "About", tid: "nav-link-about" },
  { href: "#faq", label: "FAQ", tid: "nav-link-faq" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yFloat = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden" data-testid="hero-section-b">
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-sage/10 blur-3xl"
      />
      <header className="relative z-20">
        <div className="max-w-[1360px] mx-auto flex items-center justify-between gap-6 px-6 lg:px-12 pt-6 lg:pt-8">
          <a href="#top" aria-label="Well&Truly — home" data-testid="nav-logo">
            <Logo variant="b" />
          </a>
          <nav className="hidden lg:flex items-center gap-9 text-[15px] text-forest/70">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={l.tid} className="hover:text-forest transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#contact"
            data-testid="nav-cta-book"
            className="inline-flex items-center rounded-full bg-forest px-5 py-2.5 sm:px-6 sm:py-3 text-[15px] font-semibold text-cream hover:bg-[#153024] transition-colors"
          >
            <span className="hidden sm:inline">Book a discovery call</span>
            <span className="sm:hidden">Book a call</span>
          </a>
        </div>
      </header>

      <div className="relative max-w-[1360px] mx-auto px-6 lg:px-12 pt-14 lg:pt-20 pb-24 lg:pb-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-forest/55"
          >
            <span className="w-8 h-px bg-clay" /> Health coaching · Dubai, UAE
          </motion.p>
          <h1 className="mt-6 font-displayb font-light text-forest text-[clamp(3.4rem,8vw,7rem)] leading-[1.0]">
            <span className="sr-only">Feel well. Live truly — health coaching by Akanksha Maheshwari, Dubai</span>
            <span aria-hidden="true">
              <Line delay={0.2}>Feel</Line>
              <Line delay={0.32} className="italic text-clay">well.</Line>
              <Line delay={0.5}>Live</Line>
              <Line delay={0.62} className="italic text-clay">truly.</Line>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-md text-forest/70 text-lg leading-relaxed"
          >
            Akanksha Maheshwari — UK-certified health coaching in Dubai. Simple, sustainable habits
            for energy, weight and calm, built around your real life.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-6"
          >
            <a
              href="#contact"
              data-testid="hero-cta-book"
              className="group inline-flex items-center gap-3 rounded-full bg-forest text-cream pl-8 pr-3 py-3 font-semibold hover:bg-[#153024] transition-colors"
            >
              Book a discovery call
              <span className="grid place-items-center w-9 h-9 rounded-full bg-clay text-cream transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={17} />
              </span>
            </a>
            <a
              href="#programs"
              data-testid="hero-cta-programs"
              className="text-forest underline decoration-clay decoration-2 underline-offset-8 hover:text-clay transition-colors"
            >
              Explore programs
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: yMain }} className="relative max-w-[430px] w-full mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 1.1, ease: EASE }}
            className="rounded-t-[999px] rounded-b-[2.5rem] overflow-hidden aspect-[3/4] shadow-[0_40px_100px_-40px_rgba(20,40,29,0.45)]"
          >
            <img
              src="/images/media1.jpg"
              alt="Akanksha Maheshwari, certified health coach"
              className="w-full h-full object-cover object-top"
            />
          </motion.div>
          <motion.div
            style={{ y: yFloat }}
            className="absolute -bottom-10 -left-6 sm:-left-16 w-36 sm:w-44 rounded-[1.75rem] overflow-hidden border-4 border-cream shadow-xl aspect-square"
          >
            <img src="/images/ak_w5.jpg" alt="Walking in the forest" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div style={{ y: yFloat }} className="absolute -top-8 -right-3 sm:-right-10">
            <Stamp />
          </motion.div>
          <LogoMark className="hidden" />
        </motion.div>
      </div>
    </section>
  );
}
